import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, ExternalLink, Settings, ArrowRight, Home, MapPin, Building2, Newspaper, Users, Briefcase, Phone, Cpu } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { AdminHeader, Btn, DataTable, Card, Input, Localized } from "../../components/admin/Fields";
import ImageUpload from "../../components/admin/ImageUpload";
import SEOFields from "../../components/admin/SEOFields";
import PageBuilder from "../../components/admin/PageBuilder";

/* --------------------------------------------------------------
 * SYSTEM PAGES — hard-coded React routes that live outside the
 * CMS `pages` collection. Each one lists WHERE its content lives
 * so the admin can jump directly to the right editor.
 * -------------------------------------------------------------- */
const SYSTEM_PAGES = [
  { url: "/", title: "Ana Sayfa", desc: "Hero, hakkımızda özeti, öne çıkan projeler, testimonials", icon: Home, editors: [
    { label: "Ana Sayfa İçeriği", to: "/admin/homepage" },
    { label: "Site Ayarları", to: "/admin/settings" },
  ] },
  { url: "/yenisehir", title: "İstanbul Yenişehir", desc: "Cinematic landing (11 mega proje, canlı harita, GeoJSON)", icon: MapPin, editors: [
    { label: "Landing Ayarları", to: "/admin/landing-pages", hash: "" },
    { label: "Yenişehir Projeleri", to: "/admin/yenisehir-projects" },
  ] },
  { url: "/yenisehir-projeleri", title: "Yenişehir Projeleri Listesi", desc: "Dinamik proje portfolyosu — kartlar CMS'ten", icon: MapPin, editors: [
    { label: "Yenişehir Projeleri", to: "/admin/yenisehir-projects" },
  ] },
  { url: "/realestate", title: "Fırat Real Estate", desc: "Gayrimenkul landing + Bitrix lead form + A/B hero", icon: Building2, editors: [
    { label: "Landing Ayarları", to: "/admin/landing-pages" },
    { label: "Formlar", to: "/admin/callbacks" },
  ] },
  { url: "/companies", title: "Grup Şirketleri", desc: "Fırat Group iştirakleri", icon: Building2, editors: [
    { label: "Grup Şirketleri", to: "/admin/group-companies" },
  ] },
  { url: "/projects", title: "Projeler", desc: "İnşaat & GYM projeleri portfolyosu", icon: Briefcase, editors: [
    { label: "Projeler", to: "/admin/projects" },
  ] },
  { url: "/media", title: "Medya", desc: "Haberler, duyurular, basın", icon: Newspaper, editors: [
    { label: "Haberler", to: "/admin/news" },
  ] },
  { url: "/about", title: "Hakkımızda", desc: "Kurumsal kimlik, tarihçe, ekip", icon: Users, editors: [
    { label: "Ekip", to: "/admin/team" },
    { label: "Referanslar", to: "/admin/testimonials" },
  ] },
  { url: "/careers", title: "Kariyer", desc: "İş ilanları + İş Ortağım programı", icon: Users, editors: [
    { label: "İş İlanları", to: "/admin/careers" },
  ] },
  { url: "/contact", title: "İletişim", desc: "İletişim formu + adres/harita", icon: Phone, editors: [
    { label: "Form Başvuruları", to: "/admin/contact-submissions" },
    { label: "İletişim Ayarları", to: "/admin/settings", hash: "#contact" },
  ] },
];

const empty = () => ({
  slug: "",
  parent_id: "",
  title: { tr: "", en: "", ar: "" },
  excerpt: { tr: "", en: "", ar: "" },
  content: { tr: "", en: "", ar: "" },
  hero_image: "",
  status: "published",
  blocks: [],
  seo: {},
});

const AdminPagesPage = () => {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/pages").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault?.(); setBusy(true);
    try {
      if (editing?.new) await api.post("/admin/pages", form);
      else await api.put(`/admin/pages/${editing.id}`, form);
      toast.success("Kaydedildi"); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); } finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`"${r.slug}" silinsin mi?`)) return;
    await api.delete(`/admin/pages/${r.id}`); load();
  };

  if (editing) return (
    <div data-testid="admin-pages-form">
      <AdminHeader eyebrow="Pages" title={editing.new ? "Yeni Sayfa" : "Sayfayı Düzenle"}
        action={<div className="flex gap-3">
          {!editing.new && form.slug ? (
            <a href={`/p/${form.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 border text-[0.7rem] tracking-[0.22em] uppercase font-medium border-black/15 hover:border-brand-navy" data-testid="page-preview"><ExternalLink size={12} /> Önizle</a>
          ) : null}
          <Btn variant="ghost" onClick={() => setEditing(null)}>İptal</Btn>
          <Btn onClick={save} disabled={busy} testId="page-save">Kaydet</Btn>
        </div>} />
      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Temel Bilgiler">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="page-slug" />
              <Input label="Parent ID (opsiyonel)" value={form.parent_id} onChange={(v) => setForm({ ...form, parent_id: v })} />
            </div>
            <div className="mt-6"><Localized label="Başlık" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="page-title" /></div>
            <div className="mt-6"><Localized label="Özet" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} textarea /></div>
            <div className="mt-6"><Localized label="Klasik İçerik (yalnızca bloklar boşsa gösterilir)" value={form.content} onChange={(v) => setForm({ ...form, content: v })} textarea /></div>
          </Card>
          <PageBuilder blocks={form.blocks || []} onChange={(blocks) => setForm({ ...form, blocks })} />
          <SEOFields
            value={form.seo}
            onChange={(seo) => setForm({ ...form, seo })}
            context={{
              title: form.title?.tr || form.title?.en || form.slug,
              content: [form.excerpt?.tr, form.content?.tr, form.excerpt?.en, form.content?.en].filter(Boolean).join(" \n").slice(0, 3000),
              category: "page",
              slug: form.slug,
            }}
          />
        </div>
        <div className="space-y-6">
          <Card title="Kapak Görseli">
            <ImageUpload value={form.hero_image} onChange={(v) => setForm({ ...form, hero_image: v })} testId="page-hero-image" />
          </Card>
          <Card title="Durum">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-white border border-black/15 px-4 py-3">
              <option value="published">Yayında</option>
              <option value="draft">Taslak</option>
            </select>
          </Card>
        </div>
      </form>
    </div>
  );

  return (
    <div data-testid="admin-pages-page">
      <AdminHeader eyebrow="Pages" title="Sayfalar" action={<Btn onClick={() => { setEditing({ new: true }); setForm(empty()); }} testId="new-page"><Plus size={14} /> Yeni CMS Sayfası</Btn>} />

      {/* SYSTEM PAGES — the React-route pages that come with the app */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <Cpu size={14} className="text-brand-primary" strokeWidth={1.5} />
          <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-primary font-medium">Sistem Sayfaları</div>
          <div className="flex-1 h-px bg-brand-navy/10 ml-2" />
          <div className="text-[0.66rem] text-brand-medium">{SYSTEM_PAGES.length} sayfa</div>
        </div>
        <p className="text-sm text-brand-medium mb-5 max-w-3xl">
          Bu sayfalar site kodunun parçasıdır ve içerikleri farklı admin modüllerinden yönetilir. Aşağıdaki karttan &quot;Düzenle&quot; ile ilgili modüle atlayın.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SYSTEM_PAGES.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.url} className="bg-white border border-brand-navy/10 hover:border-brand-primary transition-colors p-5 flex flex-col" data-testid={`system-page-${p.url.replace(/[^a-z0-9]+/gi, "-")}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 bg-brand-navy/5 flex items-center justify-center">
                    <Icon size={18} strokeWidth={1.5} className="text-brand-navy" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-base text-brand-navy leading-tight">{p.title}</div>
                    <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-brand-medium hover:text-brand-primary flex items-center gap-1 mt-0.5">
                      <ExternalLink size={10} /> {p.url}
                    </a>
                  </div>
                </div>
                <p className="mt-3 text-xs text-brand-dark/70 leading-relaxed flex-1">{p.desc}</p>
                <div className="mt-4 pt-3 border-t border-black/5 flex flex-wrap gap-1.5">
                  {p.editors.map((e, i) => (
                    <Link
                      key={i}
                      to={e.to + (e.hash || "")}
                      className="inline-flex items-center gap-1 text-[0.6rem] tracking-[0.22em] uppercase text-brand-primary hover:text-white hover:bg-brand-primary border border-brand-primary/25 hover:border-brand-primary px-2.5 py-1.5 transition-colors"
                      data-testid={`edit-${p.url.replace(/[^a-z0-9]+/gi, "-")}-${i}`}
                    >
                      <Settings size={10} /> {e.label} <ArrowRight size={10} />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CMS PAGES — dynamic pages from the /admin/pages collection */}
      <div className="flex items-center gap-2 mb-4">
        <Plus size={14} className="text-brand-primary" strokeWidth={1.5} />
        <div className="text-[0.66rem] tracking-[0.32em] uppercase text-brand-primary font-medium">Özel CMS Sayfaları</div>
        <div className="flex-1 h-px bg-brand-navy/10 ml-2" />
        <div className="text-[0.66rem] text-brand-medium">{rows.length} sayfa</div>
      </div>
      <p className="text-sm text-brand-medium mb-5 max-w-3xl">
        Kendi oluşturduğunuz sayfalar. Yeni bir sayfa için sağ üstteki &quot;Yeni CMS Sayfası&quot; butonunu kullanın; slug bilgisiyle <code className="text-xs bg-brand-light px-1">/p/&lt;slug&gt;</code> adresinde yayınlanır.
      </p>
      <DataTable
        columns={[
          { key: "title", label: "Başlık", render: (r) => r.title?.tr || r.slug },
          { key: "slug", label: "Slug", render: (r) => <code className="text-xs">/p/{r.slug}</code> },
          { key: "blocks", label: "Bloklar", render: (r) => (r.blocks || []).length },
          { key: "status", label: "Durum" },
          { key: "actions", label: "", render: (r) => (
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => { setEditing(r); setForm({ ...empty(), ...r, seo: r.seo || {}, blocks: r.blocks || [] }); }}>Düzenle</Btn>
              <Btn variant="danger" onClick={() => remove(r)}><Trash2 size={12} /></Btn>
            </div>
          ) },
        ]}
        rows={rows}
      />
    </div>
  );
};

export default AdminPagesPage;
